/**
 * ChatPanel — the center backdrop. Per Evan's spec (CSV): the chat opens to the
 * Daily Brief (morning) / Synthesis (evening) as the backdrop; conversation
 * appends below. The omnipresent chat bar (in SageShell) drives sends here.
 */
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import { useMcpTool } from "../lib/useMcpTool";
import { chatComplete, chatStatus, type ChatMessage } from "../lib/chat";
import BriefText from "../components/BriefText";

export interface ChatHandle {
  send: (text: string) => Promise<void>;
  generating: boolean;
}

export default forwardRef<ChatHandle, { personaPrompt: string }>(function ChatPanel({ personaPrompt }, ref) {
  const brief = useMcpTool<string>("core", "daily_brief");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const send = async (text: string) => {
    if (!text.trim() || generating) return;
    const next = [...messages, { role: "user" as const, content: text }];
    setMessages(next);
    setError(null);
    setGenerating(true);
    requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }));

    const system = [
      personaPrompt,
      "You are SAGE — Evan's whole-life operating system. Be concise, grounded, and specific.",
      typeof brief.data === "string" ? `\nToday's brief for context:\n${brief.data.slice(0, 2000)}` : "",
      `Current date: ${new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}`,
    ].join("\n");

    const result = await chatComplete(next, system);
    setGenerating(false);
    if (result.ok) {
      setMessages([...next, { role: "assistant", content: result.text }]);
    } else if (result.error === "no-key") {
      setError("Add an Anthropic or OpenRouter key in Connections to chat.");
    } else {
      setError(result.error);
    }
    requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }));
  };

  useImperativeHandle(ref, () => ({ send, generating }));

  const now = new Date();
  const isEvening = now.getHours() >= 17;
  const label = isEvening ? "Daily Synthesis" : "Daily Brief";

  return (
    <div ref={scrollRef} className="h-full overflow-auto px-4 pt-6 pb-44">
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Brief backdrop */}
        <div className="rounded-xl border bg-surface p-5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-primary" />
            <span className="font-serif text-h2 text-text">{label}</span>
            <span className="text-caption text-text-dim ml-auto">{now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</span>
          </div>
          {brief.loading ? (
            <div><div className="skeleton skeleton-text" /><div className="skeleton skeleton-text" /><div className="skeleton skeleton-text skeleton-text--short" /></div>
          ) : typeof brief.data === "string" ? (
            <BriefText text={brief.data} />
          ) : (
            <div className="text-body-sm text-text-dim">Brief unavailable. Check Connections → Test connections.</div>
          )}
        </div>

        {/* Conversation */}
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${m.role === "user" ? "bg-primary-muted text-text" : "bg-surface border"}`}>
              <div className={`${m.role === "assistant" ? "font-serif" : ""} text-body whitespace-pre-wrap leading-relaxed`}>{m.content}</div>
            </div>
          </div>
        ))}
        {generating && (
          <div className="flex justify-start">
            <div className="rounded-2xl px-4 py-2.5 bg-surface border text-text-muted text-body-sm animate-pulse-live">SAGE is thinking…</div>
          </div>
        )}
        {error && <div className="text-body-sm text-warning text-center">{error}</div>}
        {messages.length === 0 && !error && (
          <div className="text-center text-caption text-text-dim pt-2">
            Ask anything in the bar below{chatStatus().provider ? "" : " — add an Anthropic/OpenRouter key in Connections first"}.
          </div>
        )}
      </div>
    </div>
  );
});
